/**
 * 6. Home Dashboard — role-based dashboard: KPIs, task list, recent
 * records, actionable alerts, quick actions, module shortcuts.
 *
 * A money KPI is the workspace currency's total; the same figure in any other
 * currency is listed beneath it, in its own currency and not converted, never
 * added in (docs/design/currency.md R5, CUR-2).
 *
 * Arabic and English (C7). The API words its KPIs, alerts and record types
 * itself; home-text.ts puts them in the active language (alerts by code, the
 * rest by the exact text sent today), and a status is the glossary's word for
 * that kind of record, never the stored value.
 */
import { Link } from "react-router-dom";
import {
  Card,
  PageHeader,
  StatCard,
  Badge,
  DataTable,
  Skeleton,
} from "@/components/ui";
import { OtherCurrencies } from "@/components/Money";
import { QueryError } from "@/components/QueryError";
import { useApiQuery } from "@/lib/api/hooks";
import type { CurrencyAmount } from "@/lib/currency";
import { useTranslation } from "@/lib/i18n";
import { recordTypeLabel } from "@/lib/i18n/records";
import {
  formatMoney,
  formatQuantity,
  formatDate,
  formatPercent,
} from "@/lib/money";
import { alertText, kpiText, recentRecordText } from "./home-text";

interface HomeData {
  kpis: Array<{
    label: string;
    value: number;
    /** Present only on a money KPI: the currency `value` is in. */
    currency?: string;
    /** A money KPI's totals in every other currency, not converted. */
    others?: CurrencyAmount[];
    meta?: string;
    tone?: "positive" | "negative";
  }>;
  pendingApprovals: Array<{
    id: string;
    entityType: string;
    entityId: string;
    submittedBy: string;
    submittedAt: string;
  }>;
  recentRecords: Array<{
    id: string;
    type: string;
    number: string;
    status: string;
    date: string;
    total?: string;
    /** Every record type on Home has a currency (NOT NULL in all four). */
    currency: string;
  }>;
  alerts: Array<{
    code: string;
    severity: "warning" | "danger";
    message: string;
  }>;
  readinessScore: number;
  blockedItems: number;
}

export function HomeDashboardPage() {
  const { t } = useTranslation();
  const { data, isPending, error, refetch, isFetching } = useApiQuery<HomeData>(
    ["dashboard", "home"],
    "/dashboard/home",
  );

  if (isPending) return <Skeleton rows={10} />;
  if (error || !data)
    return (
      <QueryError
        error={error}
        retrying={isFetching}
        onRetry={() => void refetch()}
      />
    );

  return (
    <div>
      <PageHeader
        title={t("home.title")}
        subtitle={`${t("home.readiness", {
          score: formatPercent(data.readinessScore),
        })} · ${t("home.blockingItems", { count: data.blockedItems })}`}
        actions={
          <>
            <Link
              className="atlas-btn atlas-btn--primary"
              to="/app/sales/orders"
            >
              {t("home.newSalesOrder")}
            </Link>
            <Link className="atlas-btn" to="/app/setup/wizard">
              {t("home.continueSetup")}
            </Link>
          </>
        }
      />

      {data.alerts.length > 0 && (
        <div className="mb-4">
          {data.alerts.map((a) => (
            <Badge
              key={a.code}
              tone={a.severity === "danger" ? "danger" : "warning"}
              className="mr-2"
            >
              {alertText(t, a)}
            </Badge>
          ))}
        </div>
      )}

      <div className="atlas-grid atlas-grid--4 mb-4">
        {data.kpis.map((kpi) => (
          // A KPI carries a currency only when it is a monetary figure; a
          // count ("12 open orders") is formatted as a count.
          <StatCard
            key={kpi.label}
            label={kpiText(t, kpi.label)}
            value={
              kpi.currency
                ? formatMoney(kpi.value, kpi.currency)
                : formatQuantity(kpi.value, 0)
            }
            detail={
              kpi.currency ? <OtherCurrencies others={kpi.others} /> : undefined
            }
            meta={kpiText(t, kpi.meta)}
            tone={kpi.tone}
          />
        ))}
      </div>

      <div className="atlas-grid atlas-grid--2">
        <Card title={t("home.pendingApprovals")}>
          <DataTable
            rows={data.pendingApprovals}
            keyField="id"
            columns={[
              {
                key: "entityType",
                label: t("home.columns.type"),
                render: (r) => recordTypeLabel(t, r.entityType),
              },
              {
                key: "entityId",
                label: t("home.columns.record"),
                render: (r) => (
                  <span className="atlas-mono">{r.entityId.slice(0, 8)}</span>
                ),
              },
              { key: "submittedBy", label: t("home.columns.submittedBy") },
              {
                key: "submittedAt",
                label: t("home.columns.date"),
                render: (r) => formatDate(r.submittedAt),
              },
            ]}
          />
        </Card>
        <Card title={t("home.recentRecords")}>
          <DataTable
            rows={data.recentRecords}
            keyField="id"
            columns={[
              {
                key: "number",
                label: t("home.columns.number"),
                render: (r) => <span className="atlas-mono">{r.number}</span>,
              },
              {
                key: "type",
                label: t("home.columns.type"),
                render: (r) => recentRecordText(t, r).type,
              },
              {
                key: "status",
                label: t("home.columns.status"),
                render: (r) => (
                  <Badge
                    tone={
                      r.status === "Posted" || r.status === "Approved"
                        ? "success"
                        : "default"
                    }
                  >
                    {recentRecordText(t, r).status}
                  </Badge>
                ),
              },
              {
                key: "total",
                label: t("home.columns.total"),
                numeric: true,
                render: (r) => formatMoney(r.total, r.currency),
              },
            ]}
          />
        </Card>
      </div>
    </div>
  );
}
